import React, { FC, useCallback, useMemo, useState } from 'react';
import { Button, Card, CardBody, CardHeader, FormGroup, Input, Label } from 'reactstrap';
import { Option } from '../../../../../shared/options';
import { ProductModel } from '../../../../../shared/orders';
import { ActionButton, ElementHeader, ElementSelector, MaterialIcon } from '../../../../components';
import { JSONForm } from '../../../../components/json-form/JSONForm';
import { useTranslation } from '../../../../translations';
import { useTernaryState } from '../../../../utils';
import { CreateOption } from '../../options/createOption';
import { ProductOptionBadges } from '../../options/utils';
import { LocalSearchProvider } from '../utils';

interface ProductOptionProps {
  readonly product: ProductModel;
  readonly onUpdate: () => void;
}

const enum Mode {
  View,
  Edit,
  Create,
}

export const ProductOption: FC<ProductOptionProps> = ({ product, onUpdate }) => {
  const { ts } = useTranslation();
  const [mode, setViewMode, setEditMode, setCreateMode] = useTernaryState(
    Mode.View,
    Mode.View,
    Mode.Edit,
    Mode.Create,
  );

  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);

  const searchProvider = useMemo(
    () => new LocalSearchProvider(async () => await api.getOptions('product')),
    [],
  );

  const resetViewMode = useCallback(() => {
    setViewMode();
    setSelectedOptions([]);
  }, [setViewMode]);

  let header: React.JSX.Element | null = null;
  let body: React.JSX.Element | React.JSX.Element[] | null = null;
  switch (mode) {
    case Mode.View: {
      if (!product.deletedAt && product.canAddMoreOptions) {
        header = (
          <div className="float-end">
            <Button color="success" id="create" onClick={setEditMode} title={ts('common.add')}>
              <MaterialIcon name="add" />
            </Button>
          </div>
        );
      }

      const hasProducts = product.options.length === 0;
      body = hasProducts ? (
        <i>{ts('there_are_currently_no_product')}</i>
      ) : (
        product.options.map((u) => (
          <div id={`option${u.id}`} key={u.id}>
            <ElementHeader
              icon={{ iconName: 'widgets' }}
              rightContent={
                <>
                  <ActionButton
                    action={async () => {
                      await api.updateProductOption(product.id, u.id, u.required, !u.mutable);
                    }}
                    className="action-lock-toggle"
                    color="primary"
                    onSuccess={onUpdate}
                    outline
                    title={ts('option.mutability.toggle')}
                  >
                    <MaterialIcon name={u.mutable ? 'lock_open' : 'lock'} />
                  </ActionButton>{' '}
                  <ActionButton
                    action={async () => {
                      await api.deleteProductOption(product.id, u.id);
                    }}
                    className="action-delete"
                    color="danger"
                    onSuccess={onUpdate}
                    outline
                    title={ts('common.delete')}
                  >
                    <MaterialIcon name="delete" />
                  </ActionButton>
                </>
              }
              title={u.name}
            >
              <ProductOptionBadges mutable={u.mutable} option={u} required={u.required} />
            </ElementHeader>
          </div>
        ))
      );

      break;
    }

    case Mode.Create: {
      header = (
        <div className="float-end">
          <Button color="danger" id="cancel" onClick={setEditMode} title={ts('common.cancel')}>
            <MaterialIcon name="cancel" />
          </Button>
        </div>
      );

      body = (
        <CreateOption
          onCreate={(opt) => {
            setSelectedOptions(selectedOptions.concat(opt));
            setEditMode();
          }}
          source="product"
        />
      );

      break;
    }

    case Mode.Edit: {
      header = (
        <div className="float-end">
          <Button color="danger" id="cancel" onClick={resetViewMode} title={ts('common.cancel')}>
            <MaterialIcon name="cancel" />
          </Button>
        </div>
      );

      body = (
        <JSONForm<object, { optionIds: number[]; mutable: boolean }>
          method="post"
          onSuccess={() => {
            resetViewMode();
            onUpdate();
          }}
          path={`/api/products/${product.id}/options`}
          preSubmit={(r) => {
            r.inputs.optionIds = selectedOptions.map(({ id }) => id);
            r.inputs.mutable = !r.inputs.mutable;
          }}
        >
          <FormGroup>
            <Label for="option">{ts('option_search')}</Label>
            <ElementSelector<Option>
              defaultSelected={selectedOptions}
              id="productOptionSelector"
              onCreateClicked={setCreateMode}
              searchProvider={searchProvider}
              selectionChanged={setSelectedOptions}
            />
            <FormGroup check className="mt-10" style={{ marginLeft: '7px', marginBottom: '12px' }}>
              <Input id="required" name="required" type="checkbox" />
              <Label check for="required">
                {ts('mark_selected_options_as_required')}
              </Label>
            </FormGroup>
            {!product.category.isVendor && (
              <FormGroup
                check
                className="mt-10"
                style={{ marginLeft: '7px', marginBottom: '12px' }}
              >
                <Input id="mutable" name="mutable" type="checkbox" />
                <Label check for="mutable">
                  {ts('mark_selected_options_as_immutable')}
                </Label>
              </FormGroup>
            )}
          </FormGroup>
          <Button block color="primary" id="assignOptions">
            {ts('assign_options')}
          </Button>
        </JSONForm>
      );
    }
  }

  return (
    <Card className="mt-10" id="productOptions">
      <CardHeader>
        <div className="card-sub-header">{ts('options')}</div>
        {header}
      </CardHeader>
      <CardBody>{body}</CardBody>
    </Card>
  );
};
